import React, {useCallback, useMemo, useState} from "react";
import {Button, Center, createListCollection, Field, Input, Portal, Select, Stack} from "@chakra-ui/react";
import {userApi} from "../../api/user-api";
import {useToast} from "@chakra-ui/toast";
import {DateTime} from "luxon";
import {Tooltip} from "../../components/Tooltip";
import {useRecoilState} from "recoil";
import {showTfaCheck} from "../../components/hooks/recoilData";
import {handleKeyPress} from "../../components/hooks/HandleKeyPress";

export interface UserRegParams {
    login: string,
    password: string,
    password2: string,
    email: string,
    nickName: string,
    fio: string,
    description: string,
    tel: string,
    country: string,
    sex: number,
    dateOfBirth: string,
    passNumber: string,
    passDate: string,
    passDepartmentCode: string,
    passDepartment: string,
    passPlaceOfBirth: string,
    passAddress: string,
    codeInn: string,
    codeKpp: string,
    codeSnils: string,
}

export const sexList = createListCollection({
    items: [
        {label: "М", value: "0"},
        {label: "Ж", value: "1"},
    ],
});

export const countryList = createListCollection({
    items: [
        {value: "RU", label: "Россия", flag: "RU", continent: "Europe"},
        {value: "US", label: "United States", flag: "🇺🇸", continent: "America"},
        {value: "CA", label: "Canada", flag: "🇨🇦", continent: "America"},
        {value: "MX", label: "Mexico", flag: "🇲🇽", continent: "America"},
        {value: "BR", label: "Brazil", flag: "🇧🇷", continent: "America"},
        {value: "ZA", label: "South Africa", flag: "🇿🇦", continent: "Africa"},
        {value: "NG", label: "Nigeria", flag: "🇳🇬", continent: "Africa"},
        {value: "MA", label: "Morocco", flag: "🇲🇦", continent: "Africa"},
        {value: "EG", label: "Egypt", flag: "🇪🇬", continent: "Africa"},
        {value: "CN", label: "China", flag: "🇨🇳", continent: "Asia"},
        {value: "JP", label: "Japan", flag: "🇯🇵", continent: "Asia"},
        {value: "IN", label: "India", flag: "🇮🇳", continent: "Asia"},
        {value: "KR", label: "South Korea", flag: "🇰🇷", continent: "Asia"},
        {value: "GB", label: "United Kingdom", flag: "🇬🇧", continent: "Europe"},
        {value: "FR", label: "France", flag: "🇫🇷", continent: "Europe"},
        {value: "DE", label: "Germany", flag: "🇩🇪", continent: "Europe"},
        {value: "IT", label: "Italy", flag: "🇮🇹", continent: "Europe"},
        {value: "ES", label: "Spain", flag: "🇪🇸", continent: "Europe"},
        {value: "AU", label: "Australia", flag: "🇦🇺", continent: "Oceania"},
        {value: "NZ", label: "New Zealand", flag: "🇳🇿", continent: "Oceania"},
        {value: "FJ", label: "Fiji", flag: "🇫🇯", continent: "Oceania"},
    ],
    itemToString: (item: any) => `${item.flag} ${item.label}`,
    itemToValue: (item: any) => item.value,
});

export default function RegForm(props: {
    pageState: number,
    setPageState: React.Dispatch<React.SetStateAction<number>>
}) {
    const inputDefault: UserRegParams = useMemo(() => {
        return {
            login: "",
            password: "",
            password2: "",
            email: "",
            nickName: "",
            fio: "",
            description: "",
            tel: "",
            country: "RU",
            sex: 0,
            dateOfBirth: "",
            passNumber: "",
            passDate: "",
            passDepartmentCode: "",
            passDepartment: "",
            passPlaceOfBirth: "",
            passAddress: "",
            codeInn: "",
            codeKpp: "",
            codeSnils: "",
        }
    }, []);
    const [inputParams, setInputParams] = useState<UserRegParams>(inputDefault);
    const toast = useToast();
    const [, setTfa] = useRecoilState(showTfaCheck);
    const [loading, setLoading] = useState(false);

    const reg = useCallback(() => {
        if (inputParams.password !== inputParams.password2) {
            toast({title: "Ошибка регистрации", description: "пароли не совпадают", status: "error"});
            return
        }
        if (!inputParams.nickName)
            inputParams.nickName = inputParams.login;
        if (!inputParams.login || !inputParams.password || !inputParams.email || !inputParams.nickName || !inputParams.fio ||
            !inputParams.tel || !inputParams.country || (!inputParams.sex && inputParams.sex !== 0) || !inputParams.dateOfBirth ||
            !inputParams.passNumber || !inputParams.passDate || !inputParams.passPlaceOfBirth || !inputParams.passAddress ||
            !inputParams.codeInn) {
            toast({title: "Ошибка регистрации", description: "поля не заполнены", status: "error"});
            return
        }

        let [lastName, firstName, secondName] = inputParams.fio.split(" ");
        lastName = lastName ? lastName.trim() : "";
        firstName = firstName ? firstName.trim() : "";
        secondName = secondName ? secondName.trim() : "";
        if (!inputParams.login || !inputParams.password || !inputParams.email) {
            toast({title: "Ошибка регистрации", description: "фио указано не корректно", status: "error"});
            return
        }

        userApi.isLoginExist(inputParams.login)
            .then((o) => {
                if (o) {
                    toast({title: "Ошибка", description: "login already exist", status: "warning"});
                    return null;
                }
                return userApi.isEmailExist(inputParams.email);
            })
            .then((o) => {
                if (o) {
                    toast({title: "Ошибка", description: "email already exist", status: "warning"});
                    return null;
                }
                return userApi.isTelExist(inputParams.tel);
            })
            .then((o) => {
                if (o) {
                    toast({title: "Error", description: "tel already exist", status: "warning"});
                    return null;
                }
                return userApi.reg(inputParams.login, inputParams.password, inputParams.email, inputParams.nickName, firstName, secondName, lastName,
                    inputParams.description, inputParams.tel, inputParams.country, inputParams.sex, DateTime.fromFormat(inputParams.dateOfBirth, 'dd.MM.yyyy').toMillis(),
                    inputParams.passNumber, DateTime.fromFormat(inputParams.passDate, 'dd.MM.yyyy').toMillis(),
                    inputParams.passDepartmentCode, inputParams.passDepartment, inputParams.passPlaceOfBirth, inputParams.passAddress, inputParams.codeInn,
                    inputParams.codeKpp, inputParams.codeSnils)
            })
            .then((o) => {
                setTfa(false);
                setLoading(false);
                if (o) {
                    toast({title: "Регистрация", status: "success"})
                    setInputParams(inputDefault);
                    props.setPageState(0);
                } else {
                    toast({title: "Ошибка регистрации", description: "проверьте параметры", status: "error"});
                }
            })
            .catch((err) => {
                setTfa(false);
                setLoading(false);
                console.log("Registration error status code is: " + err)
                toast({title: "Ошибка регистрации", description: `code: [${err}]`, status: "error"});
            });
        setTfa(true);
        setLoading(true);
    }, [inputParams, setInputParams, inputDefault, toast, props, setTfa]);

    return (
        <Center>
            <Stack>
                <Center>Добро пожаловать на страницу регистрации! 👁️</Center>
                <Center>Укажите Ваши данные для регистрации на нашей платформе:</Center>
                <Field.Root orientation="horizontal" required>
                    <Field.Label>Логин <Field.RequiredIndicator/></Field.Label>
                    <Input type="text" placeholder="Логин"
                           value={inputParams.login}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, login: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Field.Label>Пароль <Field.RequiredIndicator/></Field.Label>
                    <Input
                        type="password"
                        placeholder="пароль"
                        value={inputParams.password}
                        onChange={(it) => setInputParams((prevState) => {
                            return {...prevState, password: it.target.value}
                        })}
                    />
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Field.Label>Повторите пароль 2 <Field.RequiredIndicator/></Field.Label>
                    <Input
                        type="password"
                        placeholder="пароль"
                        value={inputParams.password2}
                        onChange={(it) => setInputParams((prevState) => {
                            return {...prevState, password2: it.target.value}
                        })}
                    />
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Field.Label>E-mail (действующий) <Field.RequiredIndicator/></Field.Label>
                    <Input type="email" placeholder="email"
                           value={inputParams.email}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, email: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Field.Label>Телефон (через +7) <Field.RequiredIndicator/></Field.Label>
                    <Input type="tel" placeholder="Телефон"
                           value={inputParams.tel}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, tel: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Field.Label>ФИО <Field.RequiredIndicator/></Field.Label>
                    <Input type="text" placeholder="ФИО"
                           value={inputParams.fio}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, fio: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Tooltip showArrow content="формат dd.mm.yyyy">
                        <Field.Label>Дата рождения <Field.RequiredIndicator/></Field.Label>
                    </Tooltip>
                    <Input type="text" placeholder="Дата рождения"
                           value={inputParams.dateOfBirth}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, dateOfBirth: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Field.Label>Пол <Field.RequiredIndicator/></Field.Label>
                    <Select.Root defaultValue={[String(inputParams.sex)]} collection={sexList} size="sm" width="320px"
                                 onChange={(e: any) => setInputParams(prev => {
                                     return {
                                         ...prev, sex: parseInt(e.target.value)
                                     }
                                 })}>
                        <Select.HiddenSelect/>
                        <Select.Control>
                            <Select.Trigger>
                                <Select.ValueText placeholder="Пол"/>
                            </Select.Trigger>
                            <Select.IndicatorGroup>
                                <Select.Indicator/>
                            </Select.IndicatorGroup>
                        </Select.Control>
                        <Portal>
                            <Select.Positioner>
                                <Select.Content>
                                    {sexList.items.map((it: any) => (
                                        <Select.Item item={it} key={it.value}>
                                            {it.label}
                                            <Select.ItemIndicator/>
                                        </Select.Item>
                                    ))}
                                </Select.Content>
                            </Select.Positioner>
                        </Portal>
                    </Select.Root>
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Field.Label>Гражданство <Field.RequiredIndicator/></Field.Label>
                    <Select.Root defaultValue={[String(inputParams.country)]} collection={countryList} size="sm" width="320px"
                                 onChange={(e: any) => setInputParams(prev => {
                                     return {
                                         ...prev, country: e.target.value
                                     }
                                 })}>
                        <Select.HiddenSelect/>
                        <Select.Control>
                            <Select.Trigger>
                                <Select.ValueText placeholder="Гражданство"/>
                            </Select.Trigger>
                            <Select.IndicatorGroup>
                                <Select.Indicator/>
                            </Select.IndicatorGroup>
                        </Select.Control>
                        <Portal>
                            <Select.Positioner>
                                <Select.Content>
                                    {countryList.items.map((it: any) => (
                                        <Select.Item item={it} key={it.value}>
                                            {it.label}
                                            <Select.ItemIndicator/>
                                        </Select.Item>
                                    ))}
                                </Select.Content>
                            </Select.Positioner>
                        </Portal>
                    </Select.Root>
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Field.Label>Серия и номер паспорта <Field.RequiredIndicator/></Field.Label>
                    <Input type="text" placeholder="Паспорт"
                           value={inputParams.passNumber}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, passNumber: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Tooltip showArrow content="формат dd.mm.yyyy">
                        <Field.Label>Дата выдачи паспорта <Field.RequiredIndicator/></Field.Label>
                    </Tooltip>
                    <Input type="text" placeholder="Дата выдачи паспорта"
                           value={inputParams.passDate}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, passDate: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Field.Label>Место рождения <Field.RequiredIndicator/></Field.Label>
                    <Input type="text" placeholder="Место рождения"
                           value={inputParams.passPlaceOfBirth}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, passPlaceOfBirth: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Field.Label>Адрес регистрации <Field.RequiredIndicator/></Field.Label>
                    <Input type="text" placeholder="Адрес"
                           value={inputParams.passAddress}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, passAddress: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Field.Label>ИНН <Field.RequiredIndicator/></Field.Label>
                    <Input type="text" placeholder="ИНН"
                           value={inputParams.codeInn}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, codeInn: it.target.value}
                           })}/>
                </Field.Root>
                <Button borderRadius={0} variant="solid" colorScheme="teal" width="full" loading={loading} onClick={reg}>
                    Reg
                </Button>
                <Button borderRadius={0} variant="outline" width="full" onClick={e => props.setPageState(0)}>
                    To login
                </Button>
            </Stack>
        </Center>
    );
}